import React, { useEffect, useMemo, useState } from "react";
import {
  MapContainer,
  Marker,
  Polyline,
  TileLayer,
  useMap,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../../firebase";

/*
  DriverManagerJobList.js
  ---------------------------------------------------------------------------
  Web 工作管理頁，資料判斷依照 APP OrgAdminFleetJobsScreen.js：
  1. 先讀取 orgs/{orgId}/applications 下的日期文件。
  2. 再逐日讀取該日期的 jobs 子集合，不讀取其他公司或其他日期。
  3. 以新版 type 判斷一般工作或物流工作。
  4. 由父層透過 onOpenJob / onCreate / onMembers 接回網站路由.
*/

const text = (value) => String(value ?? "").trim();
const lower = (value) => text(value).toLowerCase();
const pad = (value) => String(value).padStart(2, "0");

function localDateKey(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const STATUS_GROUPS = {
  active: [
    "in_progress",
    "ongoing",
    "working",
    "running",
    "busy",
    "active",
    "applied",
    "has_applications",
    "outsourced",
  ],
  done: ["completed", "done", "closed", "finished"],
  cancelled: ["cancelled", "canceled", "rejected"],
};

function safeNumber(value, fallback = 0) {
  const number = Number(
    String(value ?? "")
      .replace(/,/g, "")
      .trim(),
  );
  return Number.isFinite(number) ? number : fallback;
}

function coordinateNumber(value, minimum, maximum) {
  const raw = text(value).replace(/,/g, "");
  if (!raw) return NaN;
  const number = Number(raw);
  return Number.isFinite(number) && number >= minimum && number <= maximum
    ? number
    : NaN;
}

function toDateSafe(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function timeValue(value) {
  return toDateSafe(value)?.getTime() || 0;
}

function formatCompactDate(value) {
  const date = toDateSafe(value);
  if (!date) return { date: "未定", time: "--:--" };
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  const sameDay = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();
  return {
    date: sameDay
      ? "今天"
      : isTomorrow
        ? "明天"
        : `${date.getMonth() + 1}/${date.getDate()}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
}

function getApplicationDate(application) {
  const candidates = [
    application?.schedule?.startAt,
    application?.schedule?.startTimeISO,
    application?.dateKey,
    application?.execution?.startedAt,
    application?.createdAt,
  ];
  return candidates.map(toDateSafe).find(Boolean) || null;
}

function isLogisticsRecord(record) {
  return lower(record?.type) === "logistics";
}

function getInternalStatus(record) {
  const raw = lower(record?.status || record?.execution?.status);
  if (["open", "published", "pending", "assigned", "accepted", "approved"].includes(raw)) return "pending";
  if (raw) return raw;
  if (record?.execution?.completedAt) return "completed";
  if (record?.execution?.startedAt || record?.actualStartedAt)
    return "in_progress";
  if (record?.driver?.uid) {
    return "pending";
  }
  return "pending";
}

function getApplicationTitle(application) {
  return text(application?.applicationName) || "未命名工作";
}

function isInspectionRecord(application) {
  return (
    text(application?.workType).toUpperCase() === "INSPECTION" ||
    text(application?.workMode).toUpperCase() === "ROUTE_RECORDING"
  );
}

const WORK_TYPE_LABELS = {
  A_GENERAL_LOGISTICS: "一般",
  NONE: "一般",
  CONTAINER: "貨櫃",
  B_CONTAINER_PORT: "貨櫃",
  HAZARDOUS_MATERIAL: "危險品",
  C_CHEMICAL_HAZMAT: "危險品",
  WASTE: "廢棄物",
  D_WASTE_RECYCLING: "廢棄物",
  SOIL_AGGREGATE: "土石方",
  E_SOIL_AGGREGATE: "土石方",
  HEAVY_EQUIPMENT: "大型設備",
  F_STEEL_HEAVY_EQUIPMENT: "大型設備",
  COLD_CHAIN: "冷鏈",
  G_COLD_CHAIN: "冷鏈",
  LIVE_ANIMAL: "活體動物",
  H_LIVE_ANIMAL: "活體動物",
  VEHICLE_MACHINERY: "車輛機械",
  I_VEHICLES_MACHINERY: "車輛機械",
};
function getWorkTypeLabel(application) {
  if (isInspectionRecord(application)) return "巡檢模式";
  const candidates = [
    application?.workDomain,
    application?.cargoCategoryId,
    application?.cargoCategory,
    application?.workType,
  ];
  for (const candidate of candidates) {
    const label = WORK_TYPE_LABELS[text(candidate).toUpperCase()];
    if (label) return label;
  }
  return "一般";
}
function getDriverName(application) {
  return text(application?.driver?.name) || "尚未指派";
}

function getVehicleText(application) {
  const vehicle = application?.vehicle || {};
  const type = text(vehicle?.vehicleType);
  const plate = text(vehicle?.plateNumber);
  const trailers = Array.isArray(application?.trailers) && application.trailers.length
    ? application.trailers
    : application?.trailer
      ? [application.trailer]
      : [];
  const vehicleText = [type, plate].filter(Boolean).join(" · ");
  const trailerText = trailers
    .map((trailer) =>
      [text(trailer?.vehicleType), text(trailer?.plateNumber)]
        .filter(Boolean)
        .join(" · "),
    )
    .filter(Boolean)
    .join("／");

  if (vehicleText && trailerText) return `${vehicleText} · 拖車 ${trailerText}`;
  if (vehicleText) return vehicleText;
  if (trailerText) return `拖車 · ${trailerText}`;
  return "車輛未指定";
}

function getVehicleDisplay(application) {
  const vehicle = application?.vehicle || {};
  const type = text(vehicle?.vehicleType);
  const plate = text(vehicle?.plateNumber);
  const trailers = Array.isArray(application?.trailers) && application.trailers.length
    ? application.trailers
    : application?.trailer
      ? [application.trailer]
      : [];
  const trailerText = trailers
    .map((trailer) => [text(trailer?.vehicleType), text(trailer?.plateNumber)].filter(Boolean).join(" · "))
    .filter(Boolean)
    .join("／");
  if (!plate && !type && !trailerText) return "車輛未指定";
  return (
    <span className="dmjl-vehicle-value">
      {plate ? <span className="dmjl-plate-badge">{plate}</span> : type ? <span className="dmjl-vehicle-name">{type}</span> : null}
      {plate && type ? <span className="dmjl-vehicle-type">{type}</span> : null}
      {trailerText ? <span className="dmjl-vehicle-trailers">拖車 {trailerText}</span> : null}
    </span>
  );
}

function isStartStop(stop) {
  return (
    text(stop?.id).toUpperCase() === "SRT" ||
    lower(stop?.role) === "start"
  );
}

function getSortedStops(record) {
  return (Array.isArray(record?.stops) ? record.stops : [])
    .map((stop, index) => ({ stop, index }))
    .sort((left, right) => {
      const leftOrder = Number(left.stop?.order);
      const rightOrder = Number(right.stop?.order);
      if (Number.isFinite(leftOrder) && Number.isFinite(rightOrder) && leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }
      return safeNumber(left.stop?.sequence, left.index) - safeNumber(right.stop?.sequence, right.index);
    })
    .map(({ stop }) => stop);
}

function getStopRecipients(stop) {
  return Array.isArray(stop?.recipients) ? stop.recipients : [];
}

function getContactOperation(contact) {
  const operation = lower(
    contact?.operationType || contact?.operation || contact?.contactRole || contact?.recipientRole || contact?.role || contact?.type,
  );
  const hasPickup = operation.includes("pickup") || operation.includes("取貨") || operation.includes("寄件") || operation.includes("sender");
  const hasDelivery = operation.includes("delivery") || operation.includes("送貨") || operation.includes("收件") || operation.includes("recipient");
  if (hasPickup && hasDelivery) return "both";
  if (hasPickup) {
    return "pickup";
  }
  if (hasDelivery) {
    return "delivery";
  }
  return "unknown";
}

function getContactCargo(contact) {
  if (Array.isArray(contact?.contents)) return contact.contents;
  if (Array.isArray(contact?.cargoItems)) return contact.cargoItems;
  return [];
}

function getLogisticsSummary(record) {
  const stops = getSortedStops(record);
  const contacts = stops.flatMap(getStopRecipients);
  const cargoItems = contacts.flatMap(getContactCargo);
  return {
    stationCount: stops.length,
    recipientCount: contacts.filter((contact) => ["delivery", "both"].includes(getContactOperation(contact))).length,
    senderCount: contacts.filter((contact) => ["pickup", "both"].includes(getContactOperation(contact))).length,
    cargoCount: cargoItems.length,
  };
}

function getDeliveryStopNumber(stop, deliveryIndex) {
  const stationIndex = Number(stop?.stationIndex);
  return Number.isFinite(stationIndex) && stationIndex > 0
    ? String(stationIndex)
    : String(deliveryIndex + 1);
}

function buildRoutePoints(record) {
  const stops = getSortedStops(record);
  if (stops.length) {
    const logistics = isLogisticsRecord(record);
    let deliveryIndex = 0;
    return stops.map((stop, index) => {
        const stopId = text(stop?.id).toUpperCase();
        const first = isStartStop(stop);
        const last = !logistics && stopId === "END";
        const lat = coordinateNumber(stop?.lat, -90, 90);
        const lng = coordinateNumber(stop?.lng, -180, 180);
        const locationText = text(
          stop?.text ||
            `${text(stop?.city)}${text(stop?.suburb)}${text(stop?.road)}${text(stop?.houseNumber)}`,
        );
        const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);
        const stationNumber = first
          ? ""
          : getDeliveryStopNumber(stop, deliveryIndex++);
        return {
          id: stopId || `stop_${index}`,
          label: first ? "起點" : last ? "終點" : logistics ? stopId : "終點",
          isStart: first,
          isEnd: last,
          stationNumber,
          text: locationText,
          hasCoordinates,
          hasLocation: Boolean(locationText) || hasCoordinates,
          lat,
          lng,
        };
      })
      .filter((point) => point.hasLocation);
  }

  return [];
}

function getRouteSummary(record) {
  const points = buildRoutePoints(record);
  if (!points.length) return "未提供路線";
  if (isLogisticsRecord(record)) {
    const start = points.find((point) => point.isStart);
    const deliveries = points.filter((point) => !point.isStart);
    const first = deliveries[0];
    const last = deliveries[deliveries.length - 1];
    if (!deliveries.length) return start?.text || "尚未設定配送站";
    if (start) {
      return `${start.text || "起點未定"} → ${deliveries.length} 個配送站點 → ${last.text || "配送站未定"}`;
    }
    return deliveries.length > 1
      ? `${deliveries.length} 個配送站點 · ${first.text || "配送站未定"} → ${last.text || "配送站未定"}`
      : `配送站 ${first.stationNumber} · ${first.text || "配送站未定"}`;
  }
  const start = points.find((point) => point.isStart);
  const end = points.find((point) => point.isEnd);
  if (start && end) return `${start.text || "起點未定"} → ${end.text || "終點未定"}`;
  if (start || end) return (start || end).text || "已設定位置";
  return "未提供路線";
}

function getRoutePointText(point) {
  if (point?.text) return point.text;
  if (point?.hasCoordinates) {
    return `${point.lat.toFixed(5)}, ${point.lng.toFixed(5)}`;
  }
  return "";
}

function getCargoSummary(record) {
  const summary = getLogisticsSummary(record);
  if (summary.cargoCount || summary.recipientCount || summary.senderCount) {
    return `${summary.recipientCount} 位收件人 · ${summary.senderCount} 位寄件人 · ${summary.cargoCount} 項貨物`;
  }
  const firstCargo = Array.isArray(record?.cargoItems)
    ? record.cargoItems[0]
    : null;
  return text(firstCargo?.name) || "尚未新增貨物";
}

function getStatusTheme(status) {
  const key = lower(status);
  if (["pending", "assigned", "accepted", "approved", "open", "published"].includes(key)) {
    return {
      label: "尚未開始",
      color: "#59636B",
      bg: "#F2F2F7",
      accent: "#8E8E93",
    };
  }
  if (
    ["in_progress", "ongoing", "working", "running", "busy", "active"].includes(
      key,
    )
  ) {
    return {
      label: "進行中",
      color: "#94651A",
      bg: "#FFF5DF",
      accent: "#FF9F0A",
    };
  }
  if (["completed", "done", "closed", "finished"].includes(key)) {
    return {
      label: "已完成",
      color: "#187452",
      bg: "#EDF9F0",
      accent: "#34C759",
    };
  }
  if (["cancelled", "canceled", "rejected"].includes(key)) {
    return {
      label: "已取消",
      color: "#C7352C",
      bg: "#FFF0EF",
      accent: "#FF3B30",
    };
  }
  return {
    label: "狀態未提供",
    color: "#59636B",
    bg: "#F2F2F7",
    accent: "#8E8E93",
  };
}

function Icon({ name, size = 18 }) {
  const paths = {
    search: "m16 16 5 5M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0",
    close: "m6 6 12 12M18 6 6 18",
    plus: "M12 5v14M5 12h14",
    arrow: "m9 18 6-6-6-6",
    pin: "M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0ZM15 10a3 3 0 1 1-6 0 3 3 0 0 1 6 0",
    user: "M20 21a8 8 0 0 0-16 0M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z",
    car: "M5 17h14M6 17l1-6 2-3h6l2 3 1 6M7 13h10M8 17v2M16 17v2",
    clock: "M12 7v5l3 2M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0",
    briefcase: "M4 7h16v13H4zM8 7V4h8v3M4 12h16M10 12v2h4v-2",
    truck:
      "M2 5h12v12H2zM14 9h4l4 4v4h-8M8 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0M20 18a2 2 0 1 1-4 0 2 2 0 0 1 4 0",
    map: "M9 18 3 21V6l6-3 6 3 6-3v15l-6 3-6-3ZM9 3v15M15 6v15",
    check: "m5 12 4 4L19 6",
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={paths[name] || paths.briefcase} />
    </svg>
  );
}

function FitMap({ points }) {
  const map = useMap();
  useEffect(() => {
    const valid = points
      .filter(
        (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng),
      )
      .map((point) => [point.lat, point.lng]);
    if (!valid.length) return;
    if (valid.length === 1) {
      map.setView(valid[0], 14, { animate: false });
    } else {
      map.fitBounds(valid, { padding: [22, 22], maxZoom: 14, animate: false });
    }
  }, [map, points]);
  return null;
}

function markerIcon(point, index, total) {
  const isStart = point.isStart === true;
  const isEnd =
    point.isEnd === true ||
    (index === total - 1 && total > 1 && lower(point.label) === "終點");
  const color = isStart ? "#3D8C69" : isEnd ? "#DF5A5A" : "#D88434";
  const label = isStart
    ? "起"
    : isEnd
      ? "終"
      : point.stationNumber || String(index + 1);
  return L.divIcon({
    className: "dmjl-marker-shell",
    html: `<span class="dmjl-marker" style="background:${color}">${label}</span>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
}

const osrmQueue = [];
let osrmQueueRunning = false;

function abortError() {
  const error = new Error("OSRM request aborted");
  error.name = "AbortError";
  return error;
}

function sleep(milliseconds) {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

async function pumpOsrmQueue() {
  if (osrmQueueRunning) return;
  osrmQueueRunning = true;

  while (osrmQueue.length) {
    const task = osrmQueue.shift();
    if (!task) continue;

    if (task.signal?.aborted) {
      task.reject(abortError());
      continue;
    }

    try {
      const response = await fetch(task.url, { signal: task.signal });
      if (!response.ok) throw new Error(`OSRM ${response.status}`);
      task.resolve(await response.json());
    } catch (error) {
      task.reject(error);
    }

    // 每張工作卡片依序查詢，避免同時打爆公開 OSRM 服務。
    await sleep(120);
  }

  osrmQueueRunning = false;
}

function enqueueOsrmRequest(url, signal) {
  return new Promise((resolve, reject) => {
    const task = { url, signal, resolve, reject };
    const cancel = () => {
      const index = osrmQueue.indexOf(task);
      if (index >= 0) osrmQueue.splice(index, 1);
      reject(abortError());
    };

    if (signal?.aborted) {
      reject(abortError());
      return;
    }

    signal?.addEventListener("abort", cancel, { once: true });
    osrmQueue.push(task);
    pumpOsrmQueue();
  });
}

function useOsrmRoute(points) {
  const coordinates = useMemo(
    () =>
      points
        .filter(
          (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng),
        )
        .map((point) => [point.lat, point.lng]),
    [points],
  );
  const coordinateKey = JSON.stringify(coordinates);
  const [routePath, setRoutePath] = useState([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [routeError, setRouteError] = useState(false);

  useEffect(() => {
    if (coordinates.length < 2) {
      setRoutePath([]);
      setRouteLoading(false);
      setRouteError(false);
      return undefined;
    }

    const controller = new AbortController();
    let alive = true;
    setRoutePath([]);
    setRouteLoading(true);
    setRouteError(false);

    const osrmCoordinates = coordinates
      .map(([lat, lng]) => `${lng},${lat}`)
      .join(";");
    const url = `https://router.project-osrm.org/route/v1/driving/${osrmCoordinates}?overview=full&geometries=geojson&steps=false`;

    enqueueOsrmRequest(url, controller.signal)
      .then((data) => {
        const geometry = data?.routes?.[0]?.geometry?.coordinates;
        const path = Array.isArray(geometry)
          ? geometry
              .map(([lng, lat]) => [Number(lat), Number(lng)])
              .filter(
                ([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng),
              )
          : [];
        if (alive) {
          setRoutePath(path.length > 1 ? path : []);
          setRouteError(path.length <= 1);
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError" && alive) {
          // OSRM 失敗時不畫直線，避免把非道路線誤導成實際路線。
          setRoutePath([]);
          setRouteError(true);
        }
      })
      .finally(() => {
        if (alive) setRouteLoading(false);
      });

    return () => {
      alive = false;
      controller.abort();
    };
  }, [coordinateKey]);

  return { coordinates, routePath, routeLoading, routeError };
}

function RouteMiniMap({ record, hideWhenNoCoordinates = false }) {
  const points = useMemo(() => buildRoutePoints(record), [record]);
  const mappedPoints = useMemo(
    () =>
      points.filter(
        (point) => Number.isFinite(point.lat) && Number.isFinite(point.lng),
      ),
    [points],
  );
  const { coordinates, routePath, routeLoading, routeError } =
    useOsrmRoute(mappedPoints);

  if (!coordinates.length) {
    if (hideWhenNoCoordinates) return null;
    return (
      <div className="dmjl-map-empty">
        <Icon name="map" size={22} />
        <span>尚未取得地圖座標</span>
      </div>
    );
  }

  return (
    <div className="dmjl-mini-map">
      <MapContainer
        center={coordinates[0]}
        zoom={13}
        zoomControl={false}
        scrollWheelZoom={false}
        dragging={false}
        doubleClickZoom={false}
        touchZoom={false}
        boxZoom={false}
        keyboard={false}
        attributionControl={false}
        className="dmjl-map"
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitMap
          points={
            routePath.length > 1
              ? routePath.map(([lat, lng], index) => ({
                  id: `route-${index}`,
                  lat,
                  lng,
                }))
              : mappedPoints
          }
        />
        {routePath.length > 1 && (
          <Polyline
            positions={routePath}
            pathOptions={{
              color: "#247BD1",
              weight: 4,
              opacity: 0.95,
              lineCap: "round",
              lineJoin: "round",
            }}
          />
        )}
        {mappedPoints.map((point, index) => (
          <Marker
            key={`${point.id}-${index}`}
            position={[point.lat, point.lng]}
            icon={markerIcon(point, index, mappedPoints.length)}
          />
        ))}
      </MapContainer>
      {routeLoading && (
        <span className="dmjl-map-loading">路線計算中…</span>
      )}
      {!routeLoading && routeError && (
        <span className="dmjl-map-loading is-error">OSRM 路線暫時無法取得</span>
      )}
    </div>
  );
}

function FilterChip({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      className={`dmjl-filter-chip ${active ? "is-active" : ""}`}
      onClick={onClick}
    >
      <span>{label}</span>
      <b>{count}</b>
    </button>
  );
}

function StatItem({ icon, label, value, tone }) {
  return (
    <div className="dmjl-stat-item">
      <span className="dmjl-stat-icon" style={{ color: tone || "#7B858D" }}>
        <Icon name={icon} size={16} />
      </span>
      <span>
        <small>{label}</small>
        <b>{value || "—"}</b>
      </span>
    </div>
  );
}

const menuActionStyle = {
  display: "block",
  width: "100%",
  border: 0,
  borderRadius: 7,
  padding: "8px 10px",
  background: "transparent",
  color: "#34434d",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 700,
  cursor: "pointer",
};

function JobCard({ item, onOpen, onEdit, onCopy, onDelete }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const logistics = isLogisticsRecord(item);
  const inspectionMode = !logistics && isInspectionRecord(item);
  const status = getInternalStatus(item);
  const theme = getStatusTheme(status);
  const jobDate = toDateSafe(getApplicationDate(item));
  const date = formatCompactDate(jobDate);
  const calendarDate = jobDate ? `${pad(jobDate.getMonth() + 1)}/${pad(jobDate.getDate())}` : "未定";
  const weekday = jobDate ? ["週日", "週一", "週二", "週三", "週四", "週五", "週六"][jobDate.getDay()] : "待安排";
  const dateContext = date.date === "今天" || date.date === "明天" ? date.date : weekday;
  const points = buildRoutePoints(item);
  const hasInspectionLocation = points.some((point) => point.hasLocation);
  const showMiniMap = !inspectionMode || points.some((point) => point.hasCoordinates);
  const inspectionStart = points.find((point) => point.isStart && point.hasLocation);
  const inspectionEnd = points.find((point) => point.isEnd && point.hasLocation);
  const deliveryPoints = points.filter((point) => !point.isStart);
  const logisticsSummary = getLogisticsSummary(item);
  const logisticsAddressText = logistics
    ? (() => {
        const addresses = points.map((point, index) => `${index + 1}. ${point.text || "地址未設定"}`);
        if (addresses.length <= 2) return addresses.join("、");
        return `${addresses[0]} ~ ${addresses[addresses.length - 1]}`;
      })()
    : "";
  const distance = safeNumber(item?.schedule?.distanceKm, 0) || null;
  const duration = safeNumber(item?.schedule?.durationMinutes, 0) || null;

  return (
    <article
      className="dmjl-job-card"
      style={{
        "--card-accent": theme.accent,
        gridTemplateColumns: showMiniMap ? undefined : "minmax(0, 1fr)",
      }}
      onClick={() => onOpen?.(item)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") onOpen?.(item);
      }}
    >
      <div className="dmjl-card-accent" />
      <div className="dmjl-card-main">
        <div className="dmjl-card-top">
          <div className="dmjl-time-box" aria-label={`${calendarDate} ${dateContext} ${date.time}`}>
            <b className="dmjl-date-value">{calendarDate}</b>
            <small className="dmjl-date-context">{dateContext}</small>
            <strong>{date.time}</strong>
          </div>
          <div className="dmjl-title-block">
            <div className="dmjl-title-line">
              <h3>{getApplicationTitle(item)}</h3>
              <span
                className={`dmjl-type-pill ${logistics ? "is-logistics" : ""}`}
              >
                {logistics ? "多點" : "單點"}
              </span>
              <span className="dmjl-type-pill dmjl-work-type-pill">
                {getWorkTypeLabel(item)}
              </span>
            </div>
          </div>
          <span
            className="dmjl-status"
            style={{ color: theme.color, background: theme.bg }}
          >
            <i style={{ background: theme.accent }} />
            {theme.label}
          </span>
          <div
            style={{ position: "relative", flex: "0 0 auto" }}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              aria-label="工作操作"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((value) => !value)}
              style={{
                width: 30,
                height: 30,
                border: "1px solid #e3e8eb",
                borderRadius: 8,
                background: "#fff",
                color: "#687780",
                fontSize: 19,
                lineHeight: 1,
                cursor: "pointer",
              }}
            >
              ⋯
            </button>
            {menuOpen ? (
              <div
                role="menu"
                style={{
                  position: "absolute",
                  zIndex: 20,
                  top: 34,
                  right: 0,
                  minWidth: 112,
                  padding: 5,
                  border: "1px solid #e5e9ec",
                  borderRadius: 10,
                  background: "#fff",
                  boxShadow: "0 10px 24px rgba(20,36,48,.14)",
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onCopy?.(item);
                  }}
                  style={menuActionStyle}
                >
                  複製工作
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onEdit?.(item);
                  }}
                  style={menuActionStyle}
                >
                  編輯
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    onDelete?.(item);
                  }}
                  style={{ ...menuActionStyle, color: "#c7352c" }}
                >
                  刪除
                </button>
              </div>
            ) : null}
          </div>
        </div>

        {(!inspectionMode || hasInspectionLocation) ? (
          <div className="dmjl-route-row">
            <div className={`dmjl-route-icon ${logistics ? "is-logistics" : ""}`}>
              <Icon name={logistics ? "truck" : "pin"} size={17} />
            </div>
            <div className="dmjl-route-copy">
              {logistics ? (
                <>
                  <b>{points[0]?.text || "尚未設定路線"}</b>
                  <span>
                    <i />
                    {logisticsSummary.stationCount ?? deliveryPoints.length} 個配送站點
                    {logisticsAddressText ? `（${logisticsAddressText}）` : ""}
                  </span>
                </>
              ) : inspectionMode ? (
                <>
                  <b>
                    {inspectionStart
                      ? `起點｜${getRoutePointText(inspectionStart)}`
                      : inspectionEnd
                        ? `終點｜${getRoutePointText(inspectionEnd)}`
                        : getRoutePointText(points[0])}
                  </b>
                  {inspectionStart && inspectionEnd ? (
                    <span>
                      <i />
                      終點｜{getRoutePointText(inspectionEnd)}
                    </span>
                  ) : null}
                </>
              ) : (
                <>
                  <b>{`起點｜${points[0]?.text || "起點尚未設定"}`}</b>
                  <b>{`終點｜${points[1]?.text || "終點尚未設定"}`}</b>
                </>
              )}
            </div>
          </div>
        ) : null}

        <div className="dmjl-meta-grid">
          <StatItem
            icon="user"
            label="成員"
            value={getDriverName(item)}
            tone="#71808B"
          />
          <StatItem
            icon="car"
            label="車輛"
            value={getVehicleDisplay(item)}
            tone="#71808B"
          />
          {!inspectionMode ? (
            <StatItem
              icon="briefcase"
              label="貨物"
              value={getCargoSummary(item)}
              tone={logistics ? "#7A67D8" : "#D18137"}
            />
          ) : null}
          {(distance || duration) && (
            <StatItem
              icon="clock"
              label="路線"
              value={`${distance ? `${distance.toFixed(1)} km` : "—"}${duration ? ` · ${Math.round(duration)} 分` : ""}`}
              tone="#247BD1"
            />
          )}
        </div>

        <div className="dmjl-card-bottom">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onOpen?.(item);
            }}
          >
            查看詳情 <Icon name="arrow" size={14} />
          </button>
        </div>
      </div>
      {showMiniMap ? (
        <RouteMiniMap record={item} hideWhenNoCoordinates={inspectionMode} />
      ) : null}
    </article>
  );
}

function EmptyState({ loading, error, onCreate, onRetry }) {
  if (loading) {
    return (
      <div className="dmjl-empty">
        <div className="dmjl-loading-spinner" />
        <b>正在同步工作資料…</b>
      </div>
    );
  }
  if (error) {
    return (
      <div className="dmjl-empty is-error">
        <span className="dmjl-empty-icon">
          <Icon name="briefcase" size={25} />
        </span>
        <h3>工作資料讀取失敗</h3>
        <p>{error}</p>
        <button type="button" onClick={onRetry}>
          重新讀取
        </button>
      </div>
    );
  }
  return (
    <div className="dmjl-empty">
      <h3>這一天尚未建立工作</h3>
      <p>趕快新建一筆新工作吧 ! </p>
      <button type="button" onClick={() => onCreate?.("general")}>
        <Icon name="plus" size={16} /> 建立工作
      </button>
    </div>
  );
}

export default function DriverManagerJobList({
  user: propUser,
  profile: propProfile,
  account: propAccount,
  orgId: propOrgId = "",
  onOpenJob,
  onEditJob,
  onCopyJob,
  onDeleteJob,
  onCreate,
  onMembers,
  dateRange,
  onDateRangeChange,
}) {
  const [authUser, setAuthUser] = useState(
    propUser || auth.currentUser || null,
  );
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [rangeStartDate, setRangeStartDate] = useState(
    () => dateRange?.start || localDateKey(),
  );
  const [rangeEndDate, setRangeEndDate] = useState(
    () => dateRange?.end || localDateKey(),
  );
  const [refreshKey, setRefreshKey] = useState(0);
  const [driverOrgId, setDriverOrgId] = useState("");
  const [driverOrgResolved, setDriverOrgResolved] = useState(false);

  const user = propUser || authUser;
  const profile = propProfile || propAccount || {};
  const directOrgId = text(propOrgId) || text(profile?.orgId);
  const orgId = directOrgId || driverOrgId;
  const todayKey = localDateKey();
  const rangeFrom =
    rangeStartDate <= rangeEndDate ? rangeStartDate : rangeEndDate;
  const rangeTo =
    rangeStartDate <= rangeEndDate ? rangeEndDate : rangeStartDate;

  useEffect(() => {
    if (!dateRange) return;
    if (dateRange.start && dateRange.start !== rangeStartDate)
      setRangeStartDate(dateRange.start);
    if (dateRange.end && dateRange.end !== rangeEndDate)
      setRangeEndDate(dateRange.end);
  }, [dateRange?.start, dateRange?.end, rangeStartDate, rangeEndDate]);

  useEffect(() => {
    let alive = true;
    setDriverOrgId("");
    setDriverOrgResolved(Boolean(directOrgId || !user?.uid));
    if (directOrgId || !user?.uid) return undefined;

    getDoc(doc(db, "drivers", user.uid))
      .then((snapshot) => {
        if (alive) {
          setDriverOrgId(snapshot.exists() ? text(snapshot.data()?.orgId) : "");
          setDriverOrgResolved(true);
        }
      })
      .catch((error) => {
        console.error("[DriverManagerJobList] driver profile error:", error);
        if (alive) setDriverOrgResolved(true);
      });

    return () => {
      alive = false;
    };
  }, [directOrgId, user?.uid]);

  useEffect(() => {
    if (propUser) {
      setAuthUser(propUser);
      return undefined;
    }
    return onAuthStateChanged(auth, (firebaseUser) =>
      setAuthUser(firebaseUser || null),
    );
  }, [propUser]);

  useEffect(() => {
    if (!user?.uid) {
      setApplications([]);
      setLoadError("尚未取得登入狀態。");
      setLoading(false);
      return undefined;
    }

    if (!driverOrgResolved) {
      setApplications([]);
      setLoadError("");
      setLoading(true);
      setRefreshing(false);
      return undefined;
    }

    if (!orgId) {
      setApplications([]);
      setLoadError("目前登入帳號尚未取得 drivers/{uid}.orgId。");
      setLoading(false);
      return undefined;
    }

    let alive = true;
    setLoading(true);
    setLoadError("");
    setRefreshing(false);

    async function loadJobs() {
      try {
        // 先只讀取日期索引，再讀取目前日期條件需要的 jobs。
        const dateSnapshot = await getDocs(
          collection(db, "orgs", orgId, "applications"),
        );
        const availableDateDocs = dateSnapshot.docs
          .filter((dateDoc) => /^\d{4}-\d{2}-\d{2}$/.test(dateDoc.id))
          .filter(
            (dateDoc) => dateDoc.id >= rangeFrom && dateDoc.id <= rangeTo,
          );

        const dayLists = await Promise.all(
          availableDateDocs.map(async (dateDoc) => {
            const jobsSnapshot = await getDocs(
              collection(db, "orgs", orgId, "applications", dateDoc.id, "jobs"),
            );
            return jobsSnapshot.docs.map((jobDoc) => ({
              id: jobDoc.id,
              applicationId: jobDoc.id,
              orgId,
              dateKey: dateDoc.id,
              ...jobDoc.data(),
            }));
          }),
        );

        if (!alive) return;
        const list = dayLists
          .flat()
          .filter((application) =>
            ["internal", "logistics"].includes(lower(application?.type)),
          )
          .sort(
            (a, b) =>
              timeValue(getApplicationDate(b)) -
              timeValue(getApplicationDate(a)),
          );
        setApplications(list);
        setLoadError("");
      } catch (error) {
        console.error("[DriverManagerJobList] jobs read error:", error);
        if (alive) {
          setApplications([]);
          setLoadError("目前無法讀取工作資料，請稍後再試。");
        }
      } finally {
        if (alive) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    }

    loadJobs();
    return () => {
      alive = false;
    };
  }, [driverOrgResolved, orgId, rangeFrom, rangeTo, refreshKey, user?.uid]);

  const stats = useMemo(() => {
    const statuses = applications.map(getInternalStatus);
    return {
      pending: statuses.filter((status) => status === "pending").length,
      active: statuses.filter(
        (status) =>
          STATUS_GROUPS.active.includes(status) &&
          !["open", "published"].includes(status),
      ).length,
      done: statuses.filter((status) => STATUS_GROUPS.done.includes(status))
        .length,
      logistics: applications.filter(isLogisticsRecord).length,
      general: applications.filter((item) => !isLogisticsRecord(item)).length,
    };
  }, [applications]);

  const filteredApplications = useMemo(() => {
    const query = lower(keyword);
    return applications.filter((application) => {
      const logistics = isLogisticsRecord(application);
      const type = logistics ? "logistics" : "general";
      const status = getInternalStatus(application);

      if (activeFilter === "pending" && status !== "pending") return false;
      if (
        activeFilter === "active" &&
        (!STATUS_GROUPS.active.includes(status) ||
          ["outsourced", "external_open"].includes(status))
      ) {
        return false;
      }
      if (activeFilter === "done" && !STATUS_GROUPS.done.includes(status))
        return false;
      if (activeFilter === "logistics" && type !== "logistics") return false;
      if (activeFilter === "general" && type !== "general") return false;
      if (!query) return true;

      return [
        getApplicationTitle(application),
        getRouteSummary(application),
        getDriverName(application),
        getVehicleText(application),
        getCargoSummary(application),
        application?.description,
        application?.applicationId,
        getStatusTheme(status).label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(query);
    });
  }, [activeFilter, applications, keyword]);

  const filters = [
    { key: "all", label: "全部", count: applications.length },
    { key: "general", label: "單點", count: stats.general },
    { key: "logistics", label: "多點", count: stats.logistics },
    { key: "pending", label: "尚未開始", count: stats.pending },
    { key: "active", label: "進行中", count: stats.active },
    { key: "done", label: "已完成", count: stats.done },
  ];

  const handleOpenJob = (application) => {
    if (onOpenJob) {
      onOpenJob(application);
      return;
    }
    // 沒有接路由時提供可被外層網站監聽的事件，不自行假設路由套件。
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("fleet-job-open", {
          detail: {
            applicationId: text(application.applicationId || application.id),
            logistics: isLogisticsRecord(application),
            application,
          },
        }),
      );
    }
  };

  const refresh = () => {
    setRefreshing(true);
    setRefreshKey((value) => value + 1);
  };

  const dateDescription = `日期區間・${rangeFrom} 至 ${rangeTo}`;
  const commitDateRange = (nextStart, nextEnd) => {
    const start = nextStart || todayKey;
    const end = nextEnd || start;
    setRangeStartDate(start);
    setRangeEndDate(end);
    onDateRangeChange?.({ start, end });
  };
  return (
    <div className="dmjl-root">
      <style>{styles}</style>
      <div className="dmjl-page">
        <section className="dmjl-workspace">
          <div className="dmjl-search-row">
            <div className="dmjl-search-box">
              <Icon name="search" size={17} />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜尋標題、地址、成員或車牌"
              />
              {keyword && (
                <button type="button" onClick={() => setKeyword("")}>
                  <Icon name="close" size={16} />
                </button>
              )}
            </div>
            <button
              type="button"
              className={`dmjl-refresh ${refreshing ? "is-refreshing" : ""}`}
              onClick={refresh}
              disabled={refreshing}
            >
              ↻
            </button>
          </div>
          <div className="dmjl-filter-list">
            {filters.map((filter) => (
              <FilterChip
                key={filter.key}
                label={filter.label}
                count={filter.count}
                active={activeFilter === filter.key}
                onClick={() => setActiveFilter(filter.key)}
              />
            ))}
            <div className="dmjl-filter-actions">
              {onMembers && (
                <button type="button" className="dmjl-members-button" onClick={onMembers}>
                  <Icon name="user" size={15} /> 車隊成員
                </button>
              )}
              <button type="button" className="dmjl-create-button" onClick={() => onCreate?.("choose")}>
                <Icon name="plus" size={15} /> 建立工作
              </button>
            </div>
          </div>
          <div className="dmjl-date-row">
            <label htmlFor="dmjl-range-start">工作日期</label>
            <input
              id="dmjl-range-start"
              type="date"
              aria-label="開始日期"
              value={rangeStartDate}
              onChange={(event) => commitDateRange(event.target.value || todayKey, rangeEndDate)}
            />
            <span>至</span>
            <input
              id="dmjl-range-end"
              type="date"
              aria-label="結束日期"
              value={rangeEndDate}
              onChange={(event) => commitDateRange(rangeStartDate, event.target.value || rangeStartDate)}
            />
          </div>
        </section>
        <div className="dmjl-list-heading">
          <div>
            <h2>工作列表</h2>
            <p>{keyword ? `搜尋「${keyword}」・${dateDescription}` : ``}</p>
          </div>
          <span>{filteredApplications.length} 筆</span>
        </div>

        <main className="dmjl-list">
          {filteredApplications.length ? (
            filteredApplications.map((item) => (
              <JobCard
                key={item.applicationId || item.id}
                item={item}
                onOpen={handleOpenJob}
                onEdit={onEditJob}
                onCopy={onCopyJob}
                onDelete={onDeleteJob}
              />
            ))
          ) : (
            <EmptyState
              loading={loading}
              error={loadError}
              onRetry={refresh}
              onCreate={onCreate}
            />
          )}
        </main>
      </div>
    </div>
  );
}

const styles = `
.dmjl-root{--ink:#1d2933;--muted:#78848d;--line:#e6eaed;--bg:#f6f8fa;--blue:#247bd1;min-width:0;min-height:0;height:100%;max-height:100%;flex:1 1 auto;background:transparent;padding-bottom:0;overflow-y:auto;overflow-x:hidden;box-sizing:border-box;color:var(--ink);font-family:Inter,"Noto Sans TC",-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}.dmjl-root *{box-sizing:border-box}.dmjl-root button,.dmjl-root input{font:inherit}.dmjl-root button{cursor:pointer}.dmjl-page{width:100%;min-height:100%;height:auto;overflow:visible;margin:0;padding:0 0 90px}.dmjl-hero{display:flex;align-items:flex-end;justify-content:space-between;gap:24px;margin-bottom:25px}.dmjl-hero h1{margin:0;font-size:31px;line-height:1.1;letter-spacing:-1px;font-weight:800}.dmjl-hero p{margin:10px 0 0;color:var(--muted);font-size:13px}.dmjl-hero-actions{display:flex;gap:9px;align-items:center}.dmjl-members-button,.dmjl-create-button{min-height:42px;display:flex;align-items:center;gap:7px;border-radius:10px;padding:0 15px;font-size:12px;font-weight:700}.dmjl-members-button{border:1px solid var(--line);background:#fff;color:#59666f}.dmjl-create-button{border:0;background:#247bd1;color:#fff;box-shadow:0 7px 16px rgba(36,123,209,.18)}.dmjl-workspace{padding:15px 18px 14px;border:1px solid var(--line);border-radius:15px;background:#fff;box-shadow:0 3px 13px rgba(21,36,48,.025)}.dmjl-search-row{display:flex;gap:10px}.dmjl-search-box{height:44px;display:flex;align-items:center;gap:9px;flex:1;min-width:0;border:1px solid #e1e6e9;border-radius:10px;padding:0 12px;background:#fafbfc;color:#8d989f}.dmjl-search-box input{flex:1;min-width:0;border:0;outline:0;background:transparent;color:#32414b;font-size:13px}.dmjl-search-box button{display:grid;place-items:center;border:0;background:transparent;color:#a2abb1;padding:2px}.dmjl-refresh{width:44px;height:44px;border:1px solid var(--line);border-radius:10px;background:#fff;color:#697680;font-size:22px;line-height:1}.dmjl-refresh.is-refreshing{animation:dmjl-spin .8s linear infinite}@keyframes dmjl-spin{to{transform:rotate(360deg)}}.dmjl-filter-list{display:flex;align-items:center;gap:8px;overflow:auto;padding-top:11px}.dmjl-filter-actions{display:flex;align-items:center;gap:8px;flex:0 0 auto;margin-left:auto;padding-left:10px;border-left:1px solid #f0f2f4}.dmjl-filter-actions .dmjl-members-button{min-height:31px;height:31px;padding:0 11px;border-radius:9px;box-shadow:none;font-size:11px;white-space:nowrap;background:#fff}.dmjl-filter-actions .dmjl-create-button{min-height:40px;height:40px;padding:0 16px;border-radius:11px;box-shadow:0 7px 16px rgba(36,123,209,.2);font-size:13px;white-space:nowrap;background:var(--blue)}.dmjl-filter-chip{height:31px;display:flex;align-items:center;gap:7px;border:1px solid var(--line);border-radius:9px;padding:0 11px;background:#fff;color:#4f5c65;font-size:11px;font-weight:700;white-space:nowrap}.dmjl-filter-chip b{min-width:17px;height:17px;display:grid;place-items:center;border-radius:9px;background:#f0f3f5;color:#7d8991;font-size:9px}.dmjl-filter-chip.is-active{border-color:var(--blue);background:var(--blue);color:#fff}.dmjl-filter-chip.is-active b{background:rgba(255,255,255,.2);color:#fff}.dmjl-list-heading{display:flex;align-items:center;justify-content:space-between;margin:27px 0 12px}.dmjl-list-heading h2{margin:0;font-size:19px;letter-spacing:-.3px}.dmjl-list-heading p{margin:4px 0 0;color:var(--muted);font-size:11px}.dmjl-list-heading>span{border-radius:8px;padding:7px 11px;background:#e9edf0;color:#5f6b73;font-size:11px;font-weight:800}.dmjl-list{display:grid;gap:12px}.dmjl-job-card{display:grid;grid-template-columns:minmax(0,1fr) 265px;min-height:205px;position:relative;overflow:hidden;border:1px solid var(--line);border-radius:15px;background:#fff;box-shadow:0 4px 15px rgba(19,35,47,.035);cursor:pointer;transition:transform .16s,box-shadow .16s,border-color .16s}.dmjl-job-card:hover{transform:translateY(-1px);border-color:#cfd9e0;box-shadow:0 8px 23px rgba(19,35,47,.08)}.dmjl-card-accent{position:absolute;left:0;top:0;bottom:0;width:4px;background:var(--card-accent)}.dmjl-card-main{min-width:0;padding:15px 17px 12px 20px}.dmjl-card-top{display:flex;align-items:flex-start;gap:11px}.dmjl-time-box{width:57px;height:57px;display:flex;flex-direction:column;align-items:center;justify-content:center;flex:0 0 57px;border-radius:12px;background:#202d37;color:#fff}.dmjl-time-box b{font-size:10px;color:#c9d1d6}.dmjl-time-box strong{margin-top:3px;font-size:15px}.dmjl-title-block{flex:1;min-width:0;padding-top:2px}.dmjl-title-line{display:flex;align-items:center;gap:7px;min-width:0}.dmjl-title-line h3{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;margin:0;font-size:16px;font-weight:800}.dmjl-title-block small{display:block;margin-top:6px;color:#a1aab0;font-size:10px}.dmjl-type-pill{padding:4px 7px;border-radius:7px;background:#eef1f3;color:#58656e;font-size:9px;font-weight:800;white-space:nowrap}.dmjl-work-type-pill{background:#fff3e7;color:#b46729}.dmjl-type-pill.is-logistics{background:#f0edff;color:#6651bb}.dmjl-status{display:flex;align-items:center;gap:5px;border-radius:8px;padding:6px 8px;font-size:10px;font-weight:800;white-space:nowrap}.dmjl-status i{width:6px;height:6px;border-radius:50%}.dmjl-route-row{display:flex;gap:9px;align-items:flex-start;margin-top:14px;border-radius:11px;padding:10px;background:#f5f7f8}.dmjl-route-icon{width:27px;height:27px;display:grid;place-items:center;flex:0 0 27px;border-radius:8px;background:#fff0e3;color:#c87327}.dmjl-route-icon.is-logistics{background:#f0edff;color:#6d59c8}.dmjl-route-copy{min-width:0;display:flex;flex-direction:column;gap:5px}.dmjl-route-copy b,.dmjl-route-copy span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.dmjl-route-copy b{font-size:12px;color:#34434d}.dmjl-route-copy span{font-size:11px;color:#697780}.dmjl-route-copy span i{display:inline-block;width:6px;height:6px;margin:0 6px 1px 0;border-radius:50%;background:#765ed0}.dmjl-meta-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-top:11px;border-top:1px solid var(--line);padding-top:11px}.dmjl-meta-grid .dmjl-stat-item:last-child{grid-column:auto}.dmjl-stat-item{display:flex;align-items:center;gap:7px;min-width:0}.dmjl-stat-icon{width:26px;height:26px;display:grid;place-items:center;flex:0 0 26px;border-radius:8px;background:#f2f4f5}.dmjl-stat-item>span:last-child{min-width:0}.dmjl-stat-item small,.dmjl-stat-item b{display:block;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}.dmjl-stat-item small{color:#99a2a8;font-size:9px}.dmjl-stat-item b{margin-top:3px;color:#51606a;font-size:10px}.dmjl-card-bottom{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-top:11px;border-top:1px solid var(--line);padding-top:10px;color:#9aa3a9;font-size:10px}.dmjl-card-bottom button{display:flex;align-items:center;gap:3px;border:0;background:transparent;color:#303f49;font-size:10px;font-weight:800}.dmjl-mini-map{min-width:0;min-height:205px;background:#e7ecee;position:relative}.dmjl-map{width:100%;height:100%;min-height:205px}.dmjl-map-empty{min-height:205px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:7px;background:#f0f3f4;color:#9aa4aa;font-size:11px}.dmjl-map-loading{position:absolute;right:9px;top:9px;z-index:800;border:1px solid rgba(224,230,234,.95);border-radius:7px;padding:5px 8px;background:rgba(255,255,255,.93);color:#687780;font-size:9px;box-shadow:0 3px 10px rgba(20,36,48,.1)}.dmjl-map-loading.is-error{border-color:#f2c9c3;color:#b45246;background:#fff5f2}.dmjl-marker-shell{border:0!important;background:transparent!important}.dmjl-marker{width:30px;height:30px;display:grid;place-items:center;border:2px solid #fff;border-radius:50%;color:#fff;font-size:9px;font-weight:800;box-shadow:0 3px 10px rgba(22,40,51,.28)}.dmjl-empty{min-height:260px;display:flex;flex-direction:column;align-items:center;justify-content:center;border:1px dashed #d9e0e4;border-radius:15px;background:#fff;color:#8b979e}.dmjl-empty-icon{width:55px;height:55px;display:grid;place-items:center;border-radius:17px;background:#fff0e3;color:#e77c26}.dmjl-empty h3{margin:14px 0 0;color:#34414a;font-size:16px}.dmjl-empty b{margin-top:11px;color:#68757d;font-size:12px}.dmjl-empty p{margin:7px 0 0;color:#9aa3a9;font-size:11px}.dmjl-empty button{display:flex;align-items:center;gap:5px;margin-top:16px;border:0;border-radius:9px;padding:10px 15px;background:#202d37;color:#fff;font-size:12px;font-weight:750}.dmjl-loading-spinner{width:25px;height:25px;border:3px solid #e5eaf0;border-top-color:#247bd1;border-radius:50%;animation:dmjl-spin .75s linear infinite}
@media(max-width:900px){.dmjl-page{width:min(100% - 28px,720px);padding-top:22px}.dmjl-job-card{grid-template-columns:minmax(0,1fr) 220px}.dmjl-meta-grid{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media(max-width:680px){.dmjl-page{width:calc(100% - 24px);padding-top:18px}.dmjl-hero{align-items:flex-start;flex-direction:column;gap:15px}.dmjl-hero h1{font-size:27px}.dmjl-hero-actions{width:100%}.dmjl-members-button,.dmjl-create-button{flex:1;justify-content:center}.dmjl-job-card{display:block}.dmjl-mini-map,.dmjl-map,.dmjl-map-empty{min-height:150px;height:150px}.dmjl-card-main{padding-right:15px}.dmjl-status{font-size:9px}.dmjl-meta-grid{grid-template-columns:repeat(2,minmax(0,1fr))}.dmjl-route-copy b,.dmjl-route-copy span{white-space:normal;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical}}
.dmjl-date-row{display:flex;align-items:center;gap:9px;flex-wrap:wrap;margin-top:12px;padding-top:12px;border-top:1px solid #f0f2f4;color:#697780;font-size:11px;font-weight:700}.dmjl-date-row label{color:#34434d}.dmjl-date-row select,.dmjl-date-row input{height:34px;border:1px solid #e1e6e9;border-radius:8px;padding:0 10px;background:#fff;color:#40505a;font-size:11px;font-weight:600;outline:none}.dmjl-date-row select:focus,.dmjl-date-row input:focus{border-color:#247bd1;box-shadow:0 0 0 3px rgba(36,123,209,.1)}.dmjl-date-row>span{color:#98a2a9;font-weight:500}.dmjl-empty.is-error .dmjl-empty-icon{background:#fff0ef;color:#c7554b}.dmjl-empty.is-error h3{color:#9f4038}.dmjl-empty.is-error p{max-width:360px;text-align:center;color:#b66c65}
/* FINAL JOB LIST GUTTERS: a small top/left breathing space */
.dmjl-page{padding:14px 18px 90px}
@media(max-width:900px){.dmjl-page{padding-left:14px;padding-right:14px}}
@media(max-width:680px){.dmjl-page{padding-left:12px;padding-right:12px}}

/* Larger, more readable Joblist typography */
.dmjl-hero h1{font-size:35px}.dmjl-hero p{font-size:15px}.dmjl-members-button,.dmjl-create-button{font-size:15px}.dmjl-search-box input{font-size:16px}.dmjl-filter-chip{font-size:14px}.dmjl-filter-chip b{font-size:11px}.dmjl-list-heading h2{font-size:22px}.dmjl-list-heading p,.dmjl-list-heading>span{font-size:14px}.dmjl-title-line{flex-wrap:wrap;row-gap:6px}.dmjl-title-line h3{font-size:20px}.dmjl-title-block small{font-size:13px}.dmjl-type-pill{padding:5px 8px;font-size:12px}.dmjl-status{font-size:13px}.dmjl-route-copy b{font-size:15px}.dmjl-route-copy span{font-size:14px}.dmjl-stat-item small{font-size:12px}.dmjl-stat-item b{font-size:13px}.dmjl-card-bottom{font-size:13px}.dmjl-card-bottom button{font-size:13px}.dmjl-map-loading{font-size:12px}.dmjl-map-empty{font-size:14px}.dmjl-empty h3{font-size:19px}.dmjl-empty b{font-size:15px}.dmjl-empty p{font-size:14px}
@media(max-width:680px){.dmjl-hero h1{font-size:31px}.dmjl-title-line h3{font-size:18px}.dmjl-status{font-size:12px}.dmjl-route-copy b{font-size:14px}.dmjl-route-copy span{font-size:13px}}
/* FINAL DATE FILTER POLISH */
.dmjl-date-row{margin-top:16px;padding:12px 14px;border:1px solid #e7ecef;border-radius:12px;background:linear-gradient(180deg,#fbfcfd 0%,#f6f8f9 100%);box-shadow:0 3px 10px rgba(27,43,54,.035);gap:10px;color:#63717a;font-size:13px}
.dmjl-date-row label{display:inline-flex;align-items:center;min-height:38px;color:#34434d;font-size:14px;font-weight:800;white-space:nowrap}
.dmjl-date-row select,.dmjl-date-row input{height:40px;min-width:132px;border:1px solid #dfe6ea;border-radius:10px;padding:0 11px;background:#fff;color:#34434d;font-size:14px;font-weight:650;box-shadow:0 1px 3px rgba(24,42,52,.04);transition:border-color .15s,box-shadow .15s}
.dmjl-date-row select:hover,.dmjl-date-row input:hover{border-color:#c8d5dc}
.dmjl-date-row select:focus,.dmjl-date-row input:focus{border-color:#247bd1;box-shadow:0 0 0 3px rgba(36,123,209,.12),0 2px 5px rgba(24,42,52,.05)}
.dmjl-date-row>span{display:inline-flex;align-items:center;min-height:38px;padding:0 2px;color:#89959c;font-size:13px;font-weight:700}
@media(max-width:680px){.dmjl-date-row{align-items:stretch;gap:7px;padding:10px}.dmjl-date-row label{width:100%;min-height:28px}.dmjl-date-row select,.dmjl-date-row input{flex:1;min-width:0;width:calc(50% - 20px);font-size:13px}.dmjl-date-row>span{min-height:40px}}
/* FINAL DATE FILTER FRAME REMOVED */
.dmjl-date-row{border:0;background:transparent;box-shadow:none;padding:4px 0 0;margin-top:12px}
@media(max-width:680px){.dmjl-date-row{padding:4px 0 0}}

/* Minimal readability adjustments: preserve the existing work-list layout. */
.dmjl-time-box{width:68px;height:68px;flex-basis:68px;gap:2px}
.dmjl-time-box .dmjl-date-value{font-size:17px;line-height:1.1;letter-spacing:.15px;font-variant-numeric:tabular-nums}
.dmjl-time-box .dmjl-date-context{color:#cbd4da;font-size:9px;line-height:1;font-weight:650}
.dmjl-time-box strong{font-size:15px;line-height:1.1;font-variant-numeric:tabular-nums}
.dmjl-meta-grid .dmjl-stat-item:first-child small,.dmjl-meta-grid .dmjl-stat-item:nth-child(2) small{font-size:11px;color:#77848d}
.dmjl-meta-grid .dmjl-stat-item:first-child b{color:#2f414d;font-size:14px;font-weight:800}
.dmjl-meta-grid .dmjl-stat-item:nth-child(2) b{color:#2f414d;font-size:13px;font-weight:750}
.dmjl-vehicle-value{display:inline-flex;align-items:center;gap:6px;max-width:100%;flex-wrap:wrap}
.dmjl-plate-badge{display:inline-flex;align-items:center;padding:3px 7px;border:1px solid #cbd5db;border-radius:6px;background:#f7f9fa;color:#253945;font-size:13px;line-height:1.15;font-weight:850;letter-spacing:.35px;font-variant-numeric:tabular-nums}
.dmjl-vehicle-name{color:#2f414d;font-weight:800}
.dmjl-vehicle-type,.dmjl-vehicle-trailers{color:#75828a;font-size:10px;font-weight:600}
@media(max-width:680px){.dmjl-time-box{width:60px;height:62px;flex-basis:60px}.dmjl-time-box .dmjl-date-value{font-size:15px}.dmjl-time-box strong{font-size:13px}.dmjl-plate-badge{font-size:12px}}

`;
